import L, { type LatLngTuple, type Tooltip as LeafletTooltip, type TooltipEvent } from 'leaflet'
import { Marker, Tooltip, useMap } from 'react-leaflet'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import {
  IAisVesselMapPosition,
  isValidAisMapPosition,
  resolveAisMapColor,
  useGetAisVesselMapPositions,
} from 'src/api/ais-vessels'

interface VesselLayersProps {
  customerId?: EntityId
}

const VesselLayers = ({ customerId }: VesselLayersProps) => {
  const { t: translate } = useTranslation(['ais-vessel'])
  const { customerId: routeCustomerId } = useParams()
  const resolvedCustomerId = resolveCustomerId(customerId, routeCustomerId)
  const { data } = useGetAisVesselMapPositions(resolvedCustomerId)
  const map = useMap()

  const vessels = Array.isArray(data) ? data.filter(isValidAisMapPosition) : []

  return (
    <>
      {vessels.map(vessel => {
        const heading = getHeading(vessel)
        const color = resolveAisMapColor(vessel.color)
        const position: LatLngTuple = [Number(vessel.lat), Number(vessel.lon)]
        return (
          <Marker
            key={vessel.id}
            position={position}
            icon={L.divIcon({
              html: `<img src="${createVesselSrc(color)}" style="transform:rotate(${heading - 30}deg);" onerror="this.onerror=null;this.src='/icons/vessel.svg'" />`,
              iconSize: [10, 10],
              className: 'map-icon',
            })}
            eventHandlers={{
              tooltipopen: event => bindTooltipInsideMap(map, position, (event as TooltipEvent).tooltip),
            }}
          >
            <Tooltip direction="top" offset={[0, -TOOLTIP_GAP]}>
              <div>
                <div>
                  {translate('mmsi')}: {vessel.mmsi}
                </div>
                <div>
                  {translate('boat-name')}: {vessel.name}
                </div>
                <div>
                  {translate('call-sign')}: {vessel.callSign || '-'}
                </div>
              </div>
            </Tooltip>
          </Marker>
        )
      })}
    </>
  )
}

export default VesselLayers

const resolveCustomerId = (customerId?: EntityId, routeCustomerId?: string) => {
  const value = customerId ?? routeCustomerId ?? localStorage.getItem('currentCustomerId')
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined
}

const getHeading = (vessel: IAisVesselMapPosition) => {
  if (vessel.hdg >= 0 && vessel.hdg <= 360) return vessel.hdg
  if (vessel.cog >= 0 && vessel.cog <= 360) return vessel.cog
  return 0
}

const TOOLTIP_GAP = 10
const TOOLTIP_PADDING = 12
const LEAFLET_TOOLTIP_MARGIN = 6

type TooltipDirection = 'top' | 'bottom' | 'left' | 'right'
type BoundTooltip = LeafletTooltip & { _edgeBound?: boolean }

const bindTooltipInsideMap = (map: L.Map, latlng: LatLngTuple, tooltip: LeafletTooltip) => {
  const bound = tooltip as BoundTooltip
  const reposition = () => keepVesselTooltipInsideMap(map, latlng, tooltip)
  if (bound._edgeBound) {
    reposition()
    return
  }
  bound._edgeBound = true

  const observer = new ResizeObserver(() => reposition())
  const observe = () => {
    const element = tooltip.getElement()
    if (!element) return false
    observer.observe(element)
    reposition()
    return true
  }
  if (!observe()) requestAnimationFrame(observe)

  map.on('move zoom resize', reposition)
  tooltip.once('remove', () => {
    bound._edgeBound = false
    observer.disconnect()
    map.off('move zoom resize', reposition)
  })
}

const keepVesselTooltipInsideMap = (map: L.Map, latlng: LatLngTuple, tooltip: LeafletTooltip) => {
  const element = tooltip.getElement()
  if (!element || element.offsetWidth === 0 || element.offsetHeight === 0) return

  const point = map.latLngToContainerPoint(tooltip.getLatLng() ?? latlng)
  const size = map.getSize()
  const width = element.offsetWidth
  const height = element.offsetHeight
  const needed = TOOLTIP_GAP + TOOLTIP_PADDING + LEAFLET_TOOLTIP_MARGIN

  const fits: Record<TooltipDirection, boolean> = {
    top: point.y >= height + needed,
    bottom: size.y - point.y >= height + needed,
    right: size.x - point.x >= width + needed,
    left: point.x >= width + needed,
  }
  const current = tooltip.options.direction
  const preferred: TooltipDirection[] =
    current === 'top' || current === 'bottom' || current === 'left' || current === 'right'
      ? [current, 'top', 'bottom', 'right', 'left']
      : ['top', 'bottom', 'right', 'left']
  const direction =
    preferred.find(side => fits[side]) ??
    (Object.entries(fits) as Array<[TooltipDirection, boolean]>)
      .map(([side]) => {
        const space =
          side === 'top' ? point.y : side === 'bottom' ? size.y - point.y : side === 'right' ? size.x - point.x : point.x
        return [side, space] as [TooltipDirection, number]
      })
      .sort((a, b) => b[1] - a[1])[0][0]

  let offsetX = 0
  let offsetY = 0
  if (direction === 'top' || direction === 'bottom') {
    offsetY = direction === 'top' ? -TOOLTIP_GAP : TOOLTIP_GAP
    const half = width / 2
    if (width + TOOLTIP_PADDING * 2 >= size.x) {
      offsetX = TOOLTIP_PADDING - (point.x - half)
    } else if (point.x - half < TOOLTIP_PADDING) {
      offsetX = TOOLTIP_PADDING - (point.x - half)
    } else if (point.x + half > size.x - TOOLTIP_PADDING) {
      offsetX = size.x - TOOLTIP_PADDING - (point.x + half)
    }
  } else {
    offsetX = direction === 'right' ? TOOLTIP_GAP : -TOOLTIP_GAP
    const half = height / 2
    if (height + TOOLTIP_PADDING * 2 >= size.y) {
      offsetY = TOOLTIP_PADDING - (point.y - half)
    } else if (point.y - half < TOOLTIP_PADDING) {
      offsetY = TOOLTIP_PADDING - (point.y - half)
    } else if (point.y + half > size.y - TOOLTIP_PADDING) {
      offsetY = size.y - TOOLTIP_PADDING - (point.y + half)
    }
  }

  const nextOffset = L.point(Math.round(offsetX), Math.round(offsetY))
  const currentOffset = L.point(tooltip.options.offset ?? [0, 0])
  if (tooltip.options.direction === direction && currentOffset.equals(nextOffset)) return

  tooltip.options.direction = direction
  tooltip.options.offset = nextOffset
  ;(tooltip as LeafletTooltip & { _updatePosition?: () => void })._updatePosition?.()
}

const createVesselSrc = (color: string) =>
  `data:image/svg+xml;charset=utf-8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="18.873" height="23.381" viewBox="0 0 18.873 23.381"><path d="M-18575.441-22288.533v-16.9l4.621-4.725,4.688,4.725v16.9l-4.687-2.58Z" transform="translate(4942.537 28608.883) rotate(30)" fill="${color}"/></svg>`,
  )}`
